// VerifyEmailCodeScreen.js - With fixed theme/settings buttons and improved UI
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { 
  ArrowLeft, Mail, Check, X, AlertCircle,
  Clock, RefreshCw, Moon, Sun, Settings 
} from 'lucide-react';
import LetterGlitch from '../components/LetterGlitch';
import { useUI } from '../contexts/UIContext';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../providers/TranslationProvider';
import { t, getCurrentLanguage, triggerLanguageChange, SUPPORTED_LANGUAGES } from '../i18n';

// Import logo
import Logo from '../icons/logo.svg';

// Memoize LetterGlitch for better performance
const MemoizedLetterGlitch = React.memo(LetterGlitch);

/**
 * Enhanced VerifyEmailCodeScreen with improved user experience
 * - Fixed theme toggle and settings buttons connections
 * - Added proper navigation flow
 * - Improved accessibility and user experience
 * - Consistent theming with other screens
 */
const VerifyEmailCodeScreen = () => {
  // Get the global UI context
  const { 
    navigateToScreen,
    isDarkMode: globalDarkMode,
    toggleTheme: globalToggleTheme
  } = useUI();
  
  const { verifyCode, resendCode } = useAuth();
  const { t: translate } = useTranslation();
  
  // Form states
  const [verificationCode, setVerificationCode] = useState(['', '', '', '', '', '']);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [email, setEmail] = useState('email@example.com'); // Would come from props or context
  
  // User preferences panel state
  const [showPreferences, setShowPreferences] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(() => getCurrentLanguage());
  const [reducedMotion, setReducedMotion] = useState(() => {
    return localStorage.getItem('reducedMotion') === 'true';
  });
  
  // Use the theme state from global context with fallback
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (globalDarkMode !== undefined) {
      return globalDarkMode;
    }
    // Fallback to local storage or system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  // Update local theme state when global theme changes
  useEffect(() => {
    if (globalDarkMode !== undefined) {
      setIsDarkMode(globalDarkMode);
    }
  }, [globalDarkMode]);
  
  // References
  const formRef = useRef(null);
  const containerRef = useRef(null);
  const logoRef = useRef(null);
  const titleRef = useRef(null);
  const subtitleRef = useRef(null);
  const themeIconRef = useRef(null);
  const backgroundRef = useRef(null);
  const inputRefs = useRef([]);
  
  // Mouse position for interactive background
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  // Different character sets for light and dark modes
  const charSets = useMemo(() => ({
    // Dark mode - typical hacker style with many symbols
    dark: [
      'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
      'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
      '!', '@', '#', '$', '&', '*', '(', ')', '-', '_', '+', '=', '/',
      '[', ']', '{', '}', ';', ':', '<', '>', ',', '0', '1', '2', '3',
      '4', '5', '6', '7', '8', '9', '|', '\\', '~', '^', '%'
    ],
    // Light mode - security and identity focused characters
    light: [
      // More frequent use of letters
      'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
      'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
      // Security symbols
      '✓', '✔', '⚷', '⚿', '⚹', '⛨', '⛓',
      // ID/Document symbols
      '◫', '◰', '◲', '◳', '◫', '◻', '◼', '◧', '◨',
      // Numbers for ID verification
      '0', '1', '2', '3', '4', '5', '6', '7', '8', '9'
    ]
  }), []);

  // LetterGlitch configuration with useMemo - matched with other screens
  const glitchThemes = useMemo(() => ({
    dark: {
      colors: ['#1e40af', '#3b82f6', '#60a5fa'], // Blue theme for dark mode
      textGlowColor: 'rgba(96, 165, 250, 0.8)',
      overlayOpacity: 0.7, // Increased for better text readability
      bgColor: '#0f172a', // Dark blue background color
      charSet: charSets.dark,
      glitchSpeed: reducedMotion ? 200 : 50, // Faster in dark mode, slower if reduced motion
      centerVignette: true,
      matrixIntensity: 0.8,
      verticalFade: 0.2,
      gradientColors: ["#3b82f6", "#6366f1", "#4f46e5"], // Same gradient colors
      gradientAnimationSpeed: 8
    },
    light: {
      colors: ['#3a86ff', '#3b82f6', '#4c6ef5'], // Blue theme matching app identity
      textGlowColor: 'rgba(59, 130, 246, 0.6)',
      overlayOpacity: 0.4,
      bgColor: '#f8fafc', // Light neutral background
      charSet: charSets.light,
      glitchSpeed: reducedMotion ? 250 : 100,
      centerVignette: false,
      matrixIntensity: 0.7,
      verticalFade: 0.1,
      density: reducedMotion ? 0.08 : 0.35,
      textColor: '#1e3a8a', // Darker blue text
      cursorColor: '#3b82f6',
      buttonBackground: 'linear-gradient(90deg, #3b82f6, #4c6ef5, #6366f1)',
      buttonHoverEffect: 'translateY(-2px) scale(1.02)',
      sloganGlow: 'rgba(59, 130, 246, 0.5)',
      logoGlow: 'radial-gradient(circle, rgba(59, 130, 246, 0.3), transparent)',
      glassEffect: {
        background: 'rgba(255,255,255,0.85)',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        shadow: '0 8px 32px rgba(59, 130, 246, 0.15)'
      },
      particlesColor: 'rgba(59, 130, 246, 0.3)',
      preferencesPanel: {
        background: 'rgba(255,255,255,0.95)',
        textColor: '#1e3a8a',
        border: '1px solid rgba(59, 130, 246, 0.3)',
        shadow: '0 4px 20px rgba(59, 130, 246, 0.2)'
      },
      settingsButton: {
        background: 'rgba(255,255,255,0.8)',
        hoverBackground: 'rgba(255,255,255,0.95)',
        iconColor: '#3b82f6' // Blue icon for better visibility
      },
      versionInfo: '#64748b', // Neutral slate color
      floatingParticles: true,
      lightModeWave: true,
      animations: {
        float: '8s ease-in-out infinite',
        shimmer: '12s infinite', // Slower shimmer
        gradient: '20s ease infinite' // Slower gradient
      },
      gradientColors: ["#3b82f6", "#6366f1", "#4f46e5"], // App colors for gradient
      gradientAnimationSpeed: 8
    }
  }), [charSets, reducedMotion]);

  // Get current theme
  const theme = useMemo(() => 
    isDarkMode ? glitchThemes.dark : glitchThemes.light
  , [isDarkMode, glitchThemes]);
  
  // Floating particles component for light mode
  const FloatingParticles = ({ isDarkMode }) => {
    const [particles, setParticles] = useState([]);

    useEffect(() => {
      if (isDarkMode) return;
      
      const createParticles = () => {
        const newParticles = [];
        const count = 30; // Number of particles
        
        for (let i = 0; i < count; i++) {
          // Generate various particle shapes
          const shapeType = Math.floor(Math.random() * 4); // 0: circle, 1: square, 2: triangle, 3: custom
          
          newParticles.push({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            size: Math.random() * 12 + 3, // Slightly larger particles
            opacity: Math.random() * 0.4 + 0.05, // More subtle opacity
            speedX: (Math.random() - 0.5) * 0.15, // Slower movement
            speedY: (Math.random() - 0.5) * 0.15,
            shape: shapeType,
            rotation: Math.random() * 360, // Random rotation
            rotationSpeed: (Math.random() - 0.5) * 1.5, // Rotation speed
          });
        }
        
        setParticles(newParticles);
      };
      
      createParticles();
      
      const moveParticles = () => {
        setParticles(prev => prev.map(particle => {
          let { x, y, speedX, speedY, rotation, rotationSpeed } = particle;
          
          x += speedX;
          y += speedY;
          rotation = (rotation + rotationSpeed) % 360;
          
          if (x < 0) x = 100;
          if (x > 100) x = 0;
          if (y < 0) y = 100;
          if (y > 100) y = 0;
          
          return { ...particle, x, y, rotation };
        }));
      };
      
      const intervalId = setInterval(moveParticles, 100);
      
      return () => clearInterval(intervalId);
    }, [isDarkMode]);

    if (isDarkMode) return null;
    
    return (
      <div className="floating-particles">
        {particles.map(particle => {
          // Apply shape-specific styles
          let particleStyle = {
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            opacity: particle.opacity,
            transform: `rotate(${particle.rotation}deg)`,
          };
          
          let shapeClass = "particle";
          
          switch(particle.shape) {
            case 1: // Square
              shapeClass = "particle particle-square";
              particleStyle.width = `${particle.size}px`;
              particleStyle.height = `${particle.size}px`;
              break;
            case 2: // Triangle
              shapeClass = "particle particle-triangle";
              particleStyle.borderWidth = `${particle.size}px`;
              break;
            case 3: // Custom shape (diamond)
              shapeClass = "particle particle-diamond";
              particleStyle.width = `${particle.size}px`;
              particleStyle.height = `${particle.size}px`;
              break;
            default: // Circle
              shapeClass = "particle particle-circle";
              particleStyle.width = `${particle.size}px`;
              particleStyle.height = `${particle.size}px`;
          }
          
          return (
            <div
              key={particle.id}
              className={shapeClass}
              style={particleStyle}
            />
          );
        })}
      </div>
    );
  };
  
  // Change language handler
  const handleLanguageChange = useCallback((langCode) => {
    triggerLanguageChange(langCode);
    setSelectedLanguage(langCode);
  }, []);
  
  // Toggle reduced motion handler
  const toggleReducedMotion = useCallback(() => {
    setReducedMotion(prev => {
      const newValue = !prev;
      localStorage.setItem('reducedMotion', newValue.toString());
      return newValue;
    });
  }, []);
  
  // Handle theme toggle - now connects to global context
  const toggleTheme = useCallback(() => {
    // Use the global toggle theme function if available
    if (typeof globalToggleTheme === 'function') {
      globalToggleTheme();
    } else {
      // Fallback to local theme toggle
      setIsDarkMode(prev => {
        const newTheme = !prev;
        // Save to localStorage
        localStorage.setItem('theme', newTheme ? 'dark' : 'light');
        return newTheme;
      });
    }
    
    // Animate theme icon
    if (themeIconRef.current) {
      themeIconRef.current.style.transform = 'rotate(360deg)';
      setTimeout(() => {
        if (themeIconRef.current) {
          themeIconRef.current.style.transform = 'rotate(0deg)';
        }
      }, 300);
    }
  }, [globalToggleTheme]);
  
  // Handle mouse movement for interactive background
  const handleMouseMove = useCallback((e) => {
    if (!backgroundRef.current || reducedMotion) return;
    
    const { left, top, width, height } = containerRef.current.getBoundingClientRect();
    const x = ((e.clientX - left) / width) - 0.5;
    const y = ((e.clientY - top) / height) - 0.5;
    
    setMousePosition({ x, y });
    
    // Update background with 3D effect
    backgroundRef.current.style.transform = `perspective(1000px) rotateX(${y * 1.5}deg) rotateY(${x * -1.5}deg)`;
    backgroundRef.current.style.backgroundPosition = `${50 + x * 5}% ${50 + y * 5}%`;
  }, [reducedMotion]);

  // Handle input change in verification code inputs
  const handleCodeInputChange = useCallback((index, value) => {
    // Only allow numeric values
    if (value && !/^\d*$/.test(value)) return;
    
    const newVerificationCode = [...verificationCode];
    newVerificationCode[index] = value;
    setVerificationCode(newVerificationCode);
    
    // Clear error when typing
    if (error) {
      setError('');
    }
    
    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  }, [verificationCode, error]);

  // Handle key down in verification code inputs
  const handleCodeInputKeyDown = useCallback((index, e) => {
    // Handle backspace
    if (e.key === 'Backspace') {
      if (!verificationCode[index] && index > 0) {
        // Focus previous input if current is empty
        inputRefs.current[index - 1].focus();
      }
    }
    
    // Handle arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  }, [verificationCode]);

  // Handle code paste
  const handleCodePaste = useCallback((e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();
    
    // Check if pasted content is a 6-digit number
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split('');
      setVerificationCode(digits);
      
      // Focus the last input
      inputRefs.current[5].focus();
    }
  }, []);

  // Handle form submission
  const handleVerifyCode = useCallback(async () => {
    // Check if all fields are filled
    if (verificationCode.some(digit => !digit)) {
      setError(t('verifyCode.error.incomplete', 'Please enter the complete 6-digit code'));
      shakeForm();
      return;
    }
    
    setIsLoading(true);
    
    try {
      // API call simulation
      await new Promise(resolve => setTimeout(resolve, 1200));
      
      // Verification API call would go here
      // await verifyCode(verificationCode.join(''));
      
      // Success effect
      setSuccess(true);
      
      // Redirect to reset password screen after a short delay
      setTimeout(() => {
        navigateToScreen('newPassword');
      }, 1000);
    } catch (err) {
      setError(t('verifyCode.error.invalid', 'Invalid verification code. Please try again.'));
      shakeForm();
    } finally {
      setIsLoading(false);
    }
  }, [verificationCode, navigateToScreen, t]);
  
  // Form validation animation
  const shakeForm = useCallback(() => {
    if (formRef.current) {
      formRef.current.classList.add('shake-animation');
      setTimeout(() => {
        if (formRef.current) formRef.current.classList.remove('shake-animation');
      }, 820);
    }
  }, []);

  // Handle resend code
  const handleResendCode = useCallback(async () => {
    if (resendCooldown > 0) return;
    
    try {
      // API call simulation
      await new Promise(resolve => setTimeout(resolve, 800));
      
      // Resend code API call would go here
      // await resendCode(email);
      
      // Show success message
      setError('');
      setSuccess(true);
      
      // Set cooldown
      setResendCooldown(30);
      
      // Reset verification code inputs
      setVerificationCode(['', '', '', '', '', '']);
      inputRefs.current[0].focus();
      
      // Hide success message after delay
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      setError(t('verifyCode.error.resendFailed', 'Failed to resend code. Please try again.'));
      shakeForm();
    }
  }, [resendCooldown, t]);
  
  // Cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    
    const timer = setInterval(() => {
      setResendCooldown(prev => prev - 1);
    }, 1000);
    
    return () => clearInterval(timer);
  }, [resendCooldown]);

  // Initial animations
  useEffect(() => {
    // Set initial background color
    if (containerRef.current) {
      containerRef.current.style.backgroundColor = theme.bgColor;
    }
    
    // Focus first input
    setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 500);
    
    // Staggered element animations
    const animateElements = () => {
      if (logoRef.current) {
        logoRef.current.style.opacity = '1';
        logoRef.current.style.transform = 'translateY(0)';
      }
      
      setTimeout(() => {
        if (titleRef.current) {
          titleRef.current.style.opacity = '1';
          titleRef.current.style.transform = 'translateY(0)';
        }
        
        setTimeout(() => {
          if (subtitleRef.current) {
            subtitleRef.current.style.opacity = '1';
            subtitleRef.current.style.transform = 'translateY(0)';
          }
          
          setTimeout(() => {
            if (formRef.current) {
              formRef.current.style.opacity = '1';
              formRef.current.style.transform = 'translateY(0)';
              
              // Staggered form element animations
              const formElements = formRef.current.querySelectorAll('.fade-in-element');
              formElements.forEach((el, index) => {
                setTimeout(() => {
                  el.style.opacity = '1';
                  el.style.transform = 'translateY(0)';
                }, 100 * index);
              });
            }
          }, 100);
        }, 100);
      }, 100);
    };
    
    setTimeout(animateElements, 300);
    
    // Create custom styles for enhanced UI
    if (!document.getElementById('enhancedVerifyEmailStyles')) {
      const styleElement = document.createElement('style');
      styleElement.id = 'enhancedVerifyEmailStyles';
      styleElement.textContent = `
        @keyframes float {
          0% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
          100% { transform: translateY(0px); }
        }
        
        @keyframes pulse {
          0% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.6); }
          70% { box-shadow: 0 0 0 15px rgba(59, 130, 246, 0); }
          100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0); }
        }
        
        @keyframes glow {
          0% { 
            box-shadow: 0 0 5px rgba(59, 130, 246, 0.5),
                        0 0 10px rgba(59, 130, 246, 0.3);
          }
          50% { 
            box-shadow: 0 0 20px rgba(59, 130, 246, 0.8),
                        0 0 30px rgba(59, 130, 246, 0.5);
          }
          100% { 
            box-shadow: 0 0 5px rgba(59, 130, 246, 0.5),
                        0 0 10px rgba(59, 130, 246, 0.3);
          }
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeOut {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-20px); }
        }
        
        @keyframes gradient {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        
        @keyframes gradientBackground {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes gradientMove {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes fadeInSlogan {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        @keyframes fadeOutSlogan {
          from { opacity: 1; transform: translateY(0); }
          to { opacity: 0; transform: translateY(-5px); }
        }
        
        .shake-animation {
          animation: shake 0.82s cubic-bezier(.36,.07,.19,.97) both;
        }
        
        .floating-logo {
          animation: float 6s ease-in-out infinite;
        }
        
        .logo-pulse {
          animation: pulse 2s infinite;
        }
        
        .btn-continue {
          position: relative;
          overflow: hidden;
          transition: all 0.3s;
        }
        
        .btn-shimmer {
          background: linear-gradient(
            90deg, 
            #3b82f6 0%,
            #4f46e5 45%, 
            #6366f1 50%,
            #4f46e5 55%,
            #3b82f6 100%
          );
          background-size: 200% 100%;
          animation: shimmer 5s infinite;
          color: white;
        }
        
        .btn-shimmer:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 25px -5px rgba(59, 130, 246, 0.4);
          animation: shimmer 2.5s infinite;
        }
        
        .btn-shimmer:active {
          transform: translateY(1px);
        }

        /* User preferences panel */
        .preferences-panel {
          position: absolute;
          top: 70px;
          right: 20px;
          width: 280px;
          background-color: rgba(255, 255, 255, 0.95);
          border-radius: 12px;
          box-shadow: 0 4px 20px rgba(59, 130, 246, 0.2);
          z-index: 20;
          overflow: hidden;
          transform-origin: top right;
          transition: all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
          border: 1px solid rgba(59, 130, 246, 0.3);
        }
        
        .preferences-panel-dark {
          background-color: rgba(15, 23, 42, 0.9);
          color: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        
        /* Toggle switch */
        .toggle-switch {
          width: 42px;
          height: 24px;
          border-radius: 12px;
          padding: 2px;
          cursor: pointer;
          position: relative;
          transition: background-color 0.3s;
        }
        
        .toggle-switch-on {
          background-color: #3b82f6;
        }
        
        .toggle-switch-off {
          background-color: #64748b;
        }
        
        .toggle-switch-knob {
          width: 20px;
          height: 20px;
          border-radius: 50%;
          background-color: white;
          position: absolute;
          top: 2px;
          transition: left 0.3s;
        }
        
        .toggle-switch-on .toggle-switch-knob {
          left: 20px;
        }
        
        .toggle-switch-off .toggle-switch-knob {
          left: 2px;
        }
        
        /* Theme toggle */
        .theme-toggle {
          position: absolute;
          top: 20px;
          right: 20px;
          backdrop-filter: blur(8px);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
          will-change: transform;
          border: none;
          outline: none;
        }
        
        .theme-toggle-dark {
          background: rgba(255, 255, 255, 0.1);
        }
        
        .theme-toggle-light {
          background: rgba(255, 255, 255, 0.5);
          border: 1px solid rgba(59, 130, 246, 0.3);
        }
        
        .theme-toggle:hover {
          transform: rotate(15deg);
        }
        
        .theme-toggle-dark:hover {
          background: rgba(255, 255, 255, 0.2);
        }
        
        .theme-toggle-light:hover {
          background: rgba(255, 255, 255, 0.3);
        }
        
        .theme-icon {
          transition: transform 0.3s ease;
        }
        
        /* Settings button */
        .settings-button {
          position: absolute;
          top: 20px;
          right: 70px;
          backdrop-filter: blur(8px);
          border-radius: 50%;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          z-index: 10;
          border: none;
          outline: none;
        }
        
        .settings-button-dark {
          background: rgba(255, 255, 255, 0.1);
          color: white;
        }
        
        .settings-button-light {
          background: rgba(255, 255, 255, 0.7);
          color: #3b82f6;
          border: 1px solid rgba(59, 130, 246, 0.3);
        }
        
        .settings-button:hover {
          transform: rotate(30deg);
        }
        
        .settings-button-light:hover {
          background: rgba(255, 255, 255, 0.95);
        }

        /* Content overlay */
        .content-overlay {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 2;
          pointer-events: none;
          transition: background-color 0.5s ease;
        }
        
        .content-overlay-dark {
          background: rgba(0, 0, 0, 0.7);
        }
        
        .content-overlay-light {
          background: rgba(0, 0, 0, 0); /* Completely transparent in light mode */
        }
        
        /* Clean background for light mode with enhanced contrast */
        .light-mode-wave {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(145deg, #f1f5f9, #e2e8f0, #e0f2fe, #f8fafc);
          background-size: 400% 400%;
          animation: gradientMove 20s ease infinite;
          z-index: 1;
        }
        
        /* Custom gradient animation for the background */
        .animated-gradient-bg {
          background: linear-gradient(-45deg, #3b82f6, #6366f1, #4f46e5);
          background-size: 200% 200%;
          animation: gradientBackground 15s ease infinite;
        }
        
        /* Light mode shapes */
        .light-mode-shapes {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%233B82F6' fill-opacity='0.07' fill-rule='evenodd'/%3E%3C/svg%3E");
          opacity: 0.6;
          z-index: 1;
        }
        
        .floating-particles {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          z-index: 2;
          pointer-events: none;
        }
        
        .particle {
          position: absolute;
          pointer-events: none;
          z-index: 2;
          transition: transform 0.5s ease;
        }
        
        .particle-circle {
          border-radius: 50%;
          background: rgba(59, 130, 246, 0.2);
          box-shadow: 0 0 10px rgba(59, 130, 246, 0.1);
        }
        
        .particle-square {
          border-radius: 3px;
          background: rgba(79, 70, 229, 0.15);
          box-shadow: 0 0 8px rgba(79, 70, 229, 0.1);
        }
        
        .particle-triangle {
          width: 0;
          height: 0;
          border-style: solid;
          border-color: transparent transparent rgba(99, 102, 241, 0.15) transparent;
        }
        
        .particle-diamond {
          background: rgba(59, 130, 246, 0.18);
          transform-origin: center;
          transform: rotate(45deg);
          box-shadow: 0 0 12px rgba(59, 130, 246, 0.08);
        }
        
        /* Glass effect for cards */
        .glass-effect {
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
          border-radius: 20px;
          position: relative;
          z-index: 5;
          transition: all 0.5s ease;
        }
        
        .glass-effect-dark {
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
        }
        
        .glass-effect-light {
          background: rgba(255, 255, 255, 0.9);
          border: 1px solid rgba(59, 130, 246, 0.3);
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.15), 
                      0 1px 3px rgba(59, 130, 246, 0.1);
          position: relative;
          overflow: hidden;
        }
        
        .glass-effect-light::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            rgba(255, 255, 255, 0), 
            rgba(255, 255, 255, 0.8), 
            rgba(255, 255, 255, 0)
          );
        }
        
        /* Logo container styling */
        .logo-container {
          position: relative;
          z-index: 5;
          will-change: transform, opacity;
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          margin: 0 auto;
          padding: 0 20px;
          max-width: 320px;
        }
        
        .logo-glow {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          border-radius: 50%;
          filter: blur(15px);
          z-index: -1;
          transition: background 0.5s ease;
        }
        
        .logo-glow-dark {
          background: radial-gradient(circle, rgba(96, 165, 250, 0.4) 0%, rgba(96, 165, 250, 0) 70%);
        }
        
        .logo-glow-light {
          background: radial-gradient(circle, rgba(56, 189, 248, 0.4) 0%, rgba(14, 165, 233, 0.1) 50%, rgba(14, 165, 233, 0) 70%);
          opacity: 0.8;
        }
        
        .logo-circle {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 5;
          background-color: #334155;
          transition: all 0.5s ease;
        }
        
        .logo-circle-light {
          background-color: #ffffff;
          box-shadow: 0 10px 25px rgba(14, 165, 233, 0.25),
                      0 0 15px rgba(56, 189, 248, 0.2);
          border: 1px solid rgba(56, 189, 248, 0.3);
        }
        
        .app-logo {
          width: 100px;
          height: 100px;
          object-fit: contain;
          display: block;
          margin: 0 auto;
        }
        
        /* Continue button styles */
        .continue-button {
          width: 100%;
          padding: 16px;
          border-radius: 12px;
          font-weight: 600;
          font-size: 16px;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-top: 24px;
          position: relative;
          overflow: hidden;
        }
        
        .continue-button-active {
          background: linear-gradient(90deg, #3b82f6, #4f46e5, #3b82f6);
          background-size: 200% 100%;
          animation: shimmer 5s linear infinite;
          color: white;
          box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
        }
        
        .continue-button-active:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
        }
        
        .continue-button-active:active {
          transform: translateY(1px);
          box-shadow: 0 2px 10px rgba(59, 130, 246, 0.2);
        }
        
        .continue-button-inactive-dark {
          background-color: rgba(255, 255, 255, 0.1);
          color: rgba(255, 255, 255, 0.4);
          cursor: not-allowed;
        }
        
        .continue-button-inactive-light {
          background-color: rgba(0, 0, 0, 0.05);
          color: rgba(0, 0, 0, 0.3);
          cursor: not-allowed;
        }
        
        /* Enhanced title styling */
        .verify-title {
          font-weight: 800;
          letter-spacing: -0.5px;
          position: relative;
          z-index: 5;
          text-align: center;
          max-width: 320px;
          margin: 0 auto;
          font-size: 28px;
          line-height: 1.2;
          overflow-wrap: break-word;
          word-break: break-word;
          padding: 0 10px;
          font-family: 'system-ui', -apple-system, sans-serif;
        }
        
        .verify-title-dark {
          color: white;
          text-shadow: 0 0 10px rgba(255, 255, 255, 1), 
                       0 0 20px rgba(96, 165, 250, 0.8),
                       0 0 30px rgba(59, 130, 246, 0.6),
                       0 2px 4px rgba(0, 0, 0, 0.8);
        }
        
        .verify-title-light {
          color: #1e3a8a;
          background-color: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(5px);
          border-radius: 16px;
          padding: 15px 20px; 
          box-shadow: 0 2px 10px rgba(59, 130, 246, 0.2);
          text-shadow: none;
          border: 1px solid rgba(59, 130, 246, 0.1);
        }
        
        .verify-subtitle {
          position: relative;
          z-index: 5;
          max-width: 320px;
          margin: 0 auto;
          line-height: 1.5;
          font-weight: 600;
          font-size: 16px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          overflow-wrap: break-word;
          word-break: break-word;
          padding: 0 10px;
          font-family: 'system-ui', -apple-system, sans-serif;
          text-align: center;
        }
        
        .verify-subtitle-dark {
          color: rgba(255, 255, 255, 0.8);
          text-shadow: 0 0 8px rgba(255, 255, 255, 0.8),
                      0 0 16px rgba(59, 130, 246, 0.6),
                      0 1px 3px rgba(0, 0, 0, 0.9);
        }
        
        .verify-subtitle-light {
          color: rgba(255, 255, 255, 1);
          text-shadow: 0 0 8px rgba(255, 255, 255, 0.8),
                      0 0 16px rgba(59, 130, 246, 0.7),
                      0 1px 3px rgba(0, 0, 0, 0.7);
        }
        
        .email-highlight {
          display: inline-block;
          font-weight: 700;
          padding: 2px 8px;
          border-radius: 4px;
          margin-top: 6px;
        }
        
        .email-highlight-dark {
          background-color: rgba(59, 130, 246, 0.2);
          color: white;
        }
        
        .email-highlight-light {
          background-color: rgba(59, 130, 246, 0.2);
          color: white;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }
        
        /* Verification code input styles */
        .code-input-container {
          display: flex;
          justify-content: center;
          gap: 8px;
          margin: 24px 0;
        }
        
        .code-input {
          width: 50px;
          height: 60px;
          border-radius: 12px;
          font-size: 24px;
          font-weight: 600;
          text-align: center;
          transition: all 0.2s ease;
          outline: none;
        }
        
        .code-input-dark {
          background-color: rgba(255, 255, 255, 0.05);
          color: white;
          border: 2px solid rgba(255, 255, 255, 0.1);
        }
        
        .code-input-dark:focus {
          border-color: rgba(59, 130, 246, 0.7);
          background-color: rgba(255, 255, 255, 0.1);
          box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.3);
        }
        
        .code-input-light {
          background-color: white;
          color: #1e293b;
          border: 2px solid rgba(59, 130, 246, 0.2);
          box-shadow: 0 2px 6px rgba(59, 130, 246, 0.1);
        }
        
        .code-input-light:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.2);
        }
        
        /* Resend button */
        .resend-button {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          background: none;
          border: none;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          padding: 6px 12px;
          border-radius: 8px;
          margin-top: 16px;
        }
        
        .resend-button-dark {
          color: rgba(59, 130, 246, 0.9);
        }
        
        .resend-button-dark:hover:not(:disabled) {
          background-color: rgba(59, 130, 246, 0.1);
          color: #60a5fa;
        }
        
        .resend-button-light {
          color: #3b82f6;
        }
        
        .resend-button-light:hover:not(:disabled) {
          background-color: rgba(59, 130, 246, 0.1);
        }
        
        .resend-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .resend-timer {
          font-size: 14px;
          margin-left: 4px;
          opacity: 0.8;
        }
        
        .fade-in-element {
          opacity: 0;
          transform: translateY(20px);
          transition: all 0.3s ease;
        }
      `;
      document.head.appendChild(styleElement);
    }
  }, [theme.bgColor]);

  // User preferences panel component
  const UserPreferencesPanel = () => {
    if (!showPreferences) return null;
    
    return (
      <div className={`preferences-panel ${isDarkMode ? 'preferences-panel-dark' : ''}`}>
        <div className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-medium text-lg">{t('settings.appSettings', 'Application Settings')}</h3>
            <button 
              onClick={() => setShowPreferences(false)}
              className="p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
              aria-label={t('common.close', 'Close')}
              type="button"
            >
              <X size={18} />
            </button>
          </div>
          
          <div className="space-y-4">
            {/* Theme toggle */}
            <div className="flex items-center justify-between">
              <span>{isDarkMode ? t('settings.darkMode', 'Dark Mode') : t('settings.lightMode', 'Light Mode')}</span>
              <button 
                onClick={toggleTheme}
                className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                aria-label={isDarkMode ? t('settings.switchToLightMode', 'Switch to Light Mode') : t('settings.switchToDarkMode', 'Switch to Dark Mode')}
                type="button"
              >
                {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
              </button>
            </div>
            
            {/* Language selection */}
            <div>
              <label className="block text-sm mb-1" htmlFor="language-selector">
                {t('settings.languageSelection', 'Language')}
              </label>
              <select 
                id="language-selector"
                value={selectedLanguage}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className={`w-full p-2 rounded text-sm ${
                  isDarkMode 
                    ? 'bg-slate-800 border-slate-700 text-white' 
                    : 'bg-white border-gray-300 text-slate-800'
                } border`}
              >
                {SUPPORTED_LANGUAGES.map(lang => (
                  <option key={lang.code} value={lang.code}>
                    {lang.name} {lang.flag}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Reduced Motion toggle */}
            <div className="flex items-center justify-between">
              <span>{t('accessibility.reducedMotion', 'Reduced Motion')}</span>
              <div 
                className={`toggle-switch ${reducedMotion ? 'toggle-switch-on' : 'toggle-switch-off'}`}
                onClick={toggleReducedMotion}
                role="switch"
                aria-checked={reducedMotion}
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && toggleReducedMotion()}
              >
                <div className="toggle-switch-knob"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div
      ref={containerRef}
      className="relative min-h-screen w-full overflow-hidden"
      style={{ backgroundColor: theme.bgColor }}
      onMouseMove={handleMouseMove}
    >
      {/* Light mode background elements */}
      {!isDarkMode && (
        <>
          <div className="light-mode-wave"></div>
          {theme.floatingParticles && <FloatingParticles isDarkMode={isDarkMode} />}
          <div className="light-mode-shapes"></div>
        </>
      )}
      
      {/* Interactive Background */}
      <div 
        ref={backgroundRef}
        className="interactive-background absolute inset-0 z-0"
        aria-hidden="true"
      ></div>
      
      {/* Matrix-like Background - Only in dark mode */}
      {isDarkMode && (
        <div className="absolute inset-0 z-0 overflow-hidden">
          <MemoizedLetterGlitch
            glitchColors={theme.colors}
            glitchSpeed={theme.glitchSpeed}
            centerVignette={theme.centerVignette}
            outerVignette={false}
            smooth={true}
            charSet={theme.charSet}
            density={theme.density || 0.5}
            fadeInSpeed={1.5}
            textGlowColor={theme.textGlowColor}
          />
        </div>
      )}
      
      {/* Semi-transparent overlay */}
      <div className={`content-overlay ${isDarkMode ? 'content-overlay-dark' : 'content-overlay-light'}`}></div>

      {/* Theme Toggle Button */}
      <button 
        className={`theme-toggle ${isDarkMode ? 'theme-toggle-dark' : 'theme-toggle-light'}`} 
        onClick={toggleTheme}
        aria-label={isDarkMode ? t('settings.switchToLightMode', 'Switch to Light Mode') : t('settings.switchToDarkMode', 'Switch to Dark Mode')}
        type="button"
      >
        <div ref={themeIconRef} className="theme-icon">
          {isDarkMode ? 
            <Sun size={18} color="#f8fafc" /> : 
            <Moon size={18} color="#3b82f6" />
          }
        </div>
      </button>
      
      {/* Settings Button */}
      <button
        className={`settings-button ${isDarkMode ? 'settings-button-dark' : 'settings-button-light'}`}
        onClick={() => setShowPreferences(!showPreferences)}
        aria-label={t('settings.appSettings', 'Application Settings')}
        type="button"
      >
        <Settings size={18} color={isDarkMode ? "#ffffff" : "#3b82f6"} />
      </button>
      
      {/* User Preferences Panel */}
      <UserPreferencesPanel />
      
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 py-4">
        {/* Back Button - Absolute positioned */}
        <button
          className={`absolute top-6 left-6 p-2 rounded-full transition-colors ${
            isDarkMode ? 
              'hover:bg-gray-700 text-white' : 
              'hover:bg-gray-100 text-gray-700'
          }`}
          onClick={() => navigateToScreen('forgotPassword')}
          aria-label={t('common.back', 'Back')}
          type="button"
        >
          <ArrowLeft size={24} />
        </button>
        
        {/* Logo Container */}
        <div 
          ref={logoRef}
          className="logo-container floating-logo mb-6 transform opacity-0 translate-y-8 transition-all duration-500"
          style={{ zIndex: 10 }}
        >
          <div className={`w-24 h-24 rounded-full flex items-center justify-center shadow-lg logo-pulse logo-circle ${!isDarkMode ? 'logo-circle-light' : ''}`}>
            <img src={Logo} alt={t('common.logoAlt', 'IDapps Logo')} className="w-12 h-12 object-contain" />
          </div>
          <div className={`logo-glow ${isDarkMode ? 'logo-glow-dark' : 'logo-glow-light'}`}></div>
        </div>
        
        {/* Title and Description */}
        <h1
          ref={titleRef}
          className={`verify-title ${isDarkMode ? 'verify-title-dark' : 'verify-title-light'} mb-3 transform opacity-0 translate-y-8 transition-all duration-500`}
        >
          {t('verifyCode.title', 'Verify email')}
        </h1>
        <div
          ref={subtitleRef}
          className={`verify-subtitle ${isDarkMode ? 'verify-subtitle-dark' : 'verify-subtitle-light'} mb-6 transform opacity-0 translate-y-8 transition-all duration-500`}
        >
          <span>{t('verifyCode.emailSubtitle', 'Enter the 6-digit code sent to')}</span>
          <span className={`email-highlight ${isDarkMode ? 'email-highlight-dark' : 'email-highlight-light'}`}>
            {email}
          </span>
        </div>
        
        {/* Success message */}
        {success && (
          <div className={`${isDarkMode ? 'bg-green-900/50 text-green-100' : 'bg-green-100 text-green-700'} 
            border-l-4 border-green-500 p-4 mb-6 rounded-lg flex items-center fade-in glass-effect ${isDarkMode ? 'glass-effect-dark' : 'glass-effect-light'}`}
          >
            <Check size={20} className="mr-2 flex-shrink-0" />
            <p>{t('verifyCode.success', 'A new verification code has been sent to your email.')}</p>
          </div>
        )}
        
        {/* Error message */}
        {error && (
          <div className={`${isDarkMode ? 'bg-red-900/50 text-red-100' : 'bg-red-100 text-red-700'} 
            border-l-4 border-red-500 p-4 mb-6 rounded-lg flex items-center fade-in glass-effect ${isDarkMode ? 'glass-effect-dark' : 'glass-effect-light'}`}
          >
            <AlertCircle size={20} className="mr-2 flex-shrink-0" />
            <p>{error}</p>
          </div>
        )}
        
        {/* Main Content - Glassmorphic card */}
        <div 
          ref={formRef} 
          className={`glass-effect ${isDarkMode ? 'glass-effect-dark' : 'glass-effect-light'} p-6 w-full max-w-md transition-all duration-300 opacity-0 transform translate-y-8`}
        >
          {/* Verification Code Inputs */}
          <div className="code-input-container fade-in-element">
            {verificationCode.map((digit, index) => (
              <input
                key={index}
                ref={el => inputRefs.current[index] = el}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleCodeInputChange(index, e.target.value)}
                onKeyDown={(e) => handleCodeInputKeyDown(index, e)}
                onPaste={index === 0 ? handleCodePaste : undefined}
                className={`code-input ${isDarkMode ? 'code-input-dark' : 'code-input-light'}`}
                aria-label={`Digit ${index + 1}`}
              />
            ))}
          </div>
          
          {/* Resend Code Button */}
          <div className="flex justify-center fade-in-element">
            <button
              onClick={handleResendCode}
              disabled={resendCooldown > 0}
              className={`resend-button ${isDarkMode ? 'resend-button-dark' : 'resend-button-light'}`}
              type="button"
            >
              <RefreshCw size={14} />
              {t('verifyCode.resendCode', 'Resend code')}
              {resendCooldown > 0 && (
                <span className="resend-timer">
                  ({resendCooldown}s)
                </span>
              )}
            </button>
          </div>
          
          {/* Continue Button */}
          <button 
            onClick={handleVerifyCode}
            disabled={isLoading || verificationCode.some(digit => !digit)}
            className={`continue-button fade-in-element ${
              !verificationCode.some(digit => !digit)
                ? 'continue-button-active' 
                : isDarkMode ? 'continue-button-inactive-dark' : 'continue-button-inactive-light'
            }`}
            type="button"
          >
            {isLoading ? (
              <span className="flex items-center justify-center">
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t('common.verifying', 'Verifying...')}
              </span>
            ) : t('common.verify', 'Verify')}
          </button>
        </div>
        
        {/* Email Explanation */}
        <p className={`text-sm mt-6 text-center max-w-sm ${
          isDarkMode ? 'text-gray-400' : 'text-gray-600'
        }`}>
          {t('verifyCode.emailExplanation', "Didn't receive the email? Check your spam folder or contact support if you're having trouble.")}
        </p>
        
        {/* Version info at the bottom left */}
        <div className={`absolute bottom-4 left-4 text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {t('common.version', 'IDapps v2.0.31')}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailCodeScreen;